// Batch 15 Step 2 — measurement type enum + resolver.
//
// The DB column `programs.measurement_type` (and its target-level
// override) is a real enum since the Batch 15 Step 1 migration. This
// module gives the frontend the same enum shape, plus an exact-string
// alias table that maps legacy varchar values to enum keys.
//
// Two things this file is NOT allowed to be:
//   - A substring dispatcher. The whole point of Batch 15 is to kill
//     substring matching on a free-text field. If a value doesn't
//     match the alias map exactly (case-insensitively), it resolves
//     to null and the caller renders an unset-state, not a Recorder.
//   - Two copies. If any other page needs to dispatch or picker off
//     the enum, import from here. Step 3 relocates this to a shared
//     location if a second consumer appears.

export type MeasurementType =
  | "trial_percent_correct"
  | "task_analysis"
  | "frequency"
  | "duration"
  | "latency"
  | "interval"
  | "abc_behaviour";

// Kept in sync with the migration backfill map. When adding a new
// enum value here, update both the DB migration AND this alias table
// in the same batch.
const LEGACY_ALIAS: Record<string, MeasurementType> = {
  "percent correct":    "trial_percent_correct",
  "task analysis":      "task_analysis",
  "frequency":          "frequency",
  "duration":           "duration",
  "latency":            "latency",
  "interval":           "interval",
  "interval recording": "interval",
  "abc behaviour":      "abc_behaviour",
  "abc behavior":       "abc_behaviour", // US spelling accepted (mirrors migration)
};

// Enum keys accepted directly (server may already return the enum
// value on newer endpoints; older ones still return the varchar).
const ENUM_KEYS: readonly MeasurementType[] = [
  "trial_percent_correct",
  "task_analysis",
  "frequency",
  "duration",
  "latency",
  "interval",
  "abc_behaviour",
];

/**
 * Resolve a raw string (either the new enum key or the legacy varchar
 * label) to a MeasurementType. Returns null for empty, unknown, or
 * anything that doesn't map exactly.
 *
 * There is NO substring matching. "Time on Task" resolves to null,
 * not task_analysis. Callers must handle null explicitly — the
 * dispatch in SessionData/Create renders an unset-state rather than
 * a Recorder.
 */
export function toMeasurementTypeEnum(raw: string | null | undefined): MeasurementType | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  if (trimmed === "") return null;
  // Direct enum key first (server-provided).
  if ((ENUM_KEYS as readonly string[]).includes(trimmed)) return trimmed as MeasurementType;
  // Legacy varchar via case-insensitive exact-string lookup.
  const lower = trimmed.toLowerCase();
  return LEGACY_ALIAS[lower] ?? null;
}
